import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Server, ShieldCheck, Database, Clock, HardDriveDownload, RotateCcw, Trash2, Download,
  CheckCircle2, AlertTriangle, XCircle, Loader2, Upload, Users, ScrollText, FileCheck,
} from "lucide-react";
import { api } from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const kb = (b) => (b == null ? "—" : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const ValidationBadge = ({ status }) => {
  const map = {
    valid: ["bg-teal-50 text-teal-700 border-teal-200", CheckCircle2, "Valid"],
    valid_with_warnings: ["bg-amber-50 text-amber-700 border-amber-200", AlertTriangle, "Warnings"],
    invalid: ["bg-red-50 text-red-700 border-red-200", XCircle, "Invalid"],
  };
  const [cls, Icon, label] = map[status] || map.invalid;
  return <Badge variant="outline" className={`gap-1 ${cls}`}><Icon size={12} /> {label}</Badge>;
};

const Health = ({ status }) => {
  const ok = status === "healthy";
  const un = status === "unavailable";
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${ok ? "text-teal-700" : un ? "text-red-700" : "text-amber-700"}`}>
      <span className={`h-2 w-2 rounded-full ${ok ? "bg-teal-500" : un ? "bg-red-500" : "bg-amber-500"}`} /> {status}
    </span>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between border-b border-[#EEF2FB] py-2 last:border-0">
    <span className="text-sm text-[#5A6B8C]">{label}</span>
    <span className="text-sm font-medium text-[#14245F]">{value}</span>
  </div>
);

const Card = ({ icon: Icon, title, children, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-[#E4E9F3] bg-white p-5">
    <div className="mb-3 flex items-center gap-2">
      <Icon size={16} className="text-[#1D4ED8]" />
      <h3 className="font-heading text-base font-semibold text-[#14245F]">{title}</h3>
    </div>
    {children}
  </div>
);

export default function SystemSettings() {
  const [ov, setOv] = useState(null);
  const [backups, setBackups] = useState([]);
  const [busy, setBusy] = useState(false);
  const [restoreFor, setRestoreFor] = useState(null);
  const [uploadReport, setUploadReport] = useState(null);

  const load = useCallback(async () => {
    const [o, b] = await Promise.all([api.settingsOverview(), api.listBackups()]);
    setOv(o); setBackups(b);
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try { await api.makeBackup(); toast.success("Backup generated & validated"); await load(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : "Backup failed. Please try again."); }
    finally { setBusy(false); }
  };

  const validate = async (f) => {
    const r = await api.validateBackup(f);
    toast[r.status === "invalid" ? "error" : "success"](`Validation: ${r.status}`);
    await load();
  };

  const del = async (f) => {
    try { await api.deleteBackup(f); toast.success("Backup deleted"); await load(); }
    catch (e) { const d = e.response?.data?.detail; if (typeof d === "string") toast.error(d); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { setUploadReport(await api.validateUpload(file)); }
    catch (er) { const d = er.response?.data?.detail; toast.error(typeof d === "string" ? d : "Validation failed"); }
    e.target.value = "";
  };

  if (!ov) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#1D4ED8]" /></div>;
  const s = ov.system, a = ov.auth;

  return (
    <>
      <PageHeader eyebrow="Administration" title="System Settings" description="System health, user summary, and full-database backup & restore. Keep downloaded backups outside this environment." />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card icon={Server} title="System Overview" testid="settings-system-overview">
          <Row label="Environment" value={s.environment} />
          <Row label="Application version" value={s.app_version} />
          <Row label="Backend" value={<Health status={s.backend_status} />} />
          <Row label="Database" value={<Health status={s.database_status} />} />
          <Row label="Database name" value={s.database_name} />
          <Row label="Collections" value={s.collection_count} />
          <Row label="Active records" value={s.active_records} />
          <Row label="Soft-deleted records" value={s.soft_deleted_records} />
          <Row label="Session duration" value={`${s.session_duration_hours} hours`} />
          <Row label="Authentication" value={s.authentication_enabled ? "Enabled" : "Disabled"} />
          <Row label="Audit logging" value={s.audit_logging_enabled ? "Enabled" : "Disabled"} />
          <Row label="Soft deletion" value={s.soft_deletion_enabled ? "Enabled" : "Disabled"} />
          <Row label="Last backup" value={fmt(s.last_backup_at)} />
          <Row label="Last restore" value={fmt(s.last_restore_at)} />
          <Row label="Server time" value={fmt(s.server_time)} />
        </Card>

        <div className="space-y-6">
          <Card icon={Users} title="Authentication & Users" testid="settings-auth-summary">
            <div className="grid grid-cols-2 gap-x-6">
              <Row label="Total users" value={a.total_users} />
              <Row label="Active" value={a.active_users} />
              <Row label="Inactive" value={a.inactive_users} />
              <Row label="Administrators" value={a.administrators} />
              <Row label="Editors" value={a.editors} />
              <Row label="Viewers" value={a.viewers} />
              <Row label="Awaiting reset" value={a.must_reset} />
              <Row label="Locked accounts" value={a.locked_accounts} />
              <Row label="Failed logins 24h" value={a.failed_logins_24h} />
              <Row label="Logins 24h" value={a.successful_logins_24h} />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/admin/users"><Button size="sm" variant="outline" data-testid="link-user-management">User Management</Button></Link>
              <Link to="/admin/audit"><Button size="sm" variant="outline" data-testid="link-audit-log">Audit Log</Button></Link>
              <Link to="/admin/recycle-bin"><Button size="sm" variant="outline" data-testid="link-recycle-bin">Recycle Bin</Button></Link>
            </div>
          </Card>

          <Card icon={ShieldCheck} title="Security Status" testid="settings-security-status">
            <Row label="CORS" value={s.cors || "Configured"} />
            <Row label="Admin endpoints" value="Protected" />
            <Row label="Backup & restore endpoints" value="Admin-only" />
            <Row label="Active lockouts" value={a.locked_accounts} />
          </Card>
        </div>
      </div>

      <Card icon={HardDriveDownload} title="Backup & Restore" testid="settings-backup-restore" >
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Button data-testid="generate-backup-btn" onClick={generate} disabled={busy} className="gap-2 bg-[#1D4ED8] hover:bg-[#1E40AF]">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <HardDriveDownload size={15} />} Generate Backup
          </Button>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-[#E4E9F3] px-3 py-2 text-sm font-medium text-[#14245F] hover:bg-[#F5F8FF]">
            <Upload size={15} /> Validate a file
            <input type="file" accept=".json,application/json" className="hidden" data-testid="validate-upload-input" onChange={onUpload} />
          </label>
        </div>
        <p className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle size={14} /> Store downloaded backups outside this environment (e.g. encrypted cloud or secure company storage). Backups are not automatically uploaded anywhere.
        </p>

        {uploadReport && (
          <div data-testid="upload-validation-report" className="mb-4 rounded-lg border border-[#E4E9F3] bg-[#F9FBFF] p-3 text-sm">
            <div className="mb-1 flex items-center gap-2"><FileCheck size={15} /> Uploaded file: <ValidationBadge status={uploadReport.status} /></div>
            {uploadReport.errors?.length > 0 && <p className="text-red-700">Errors: {uploadReport.errors.join("; ")}</p>}
            {uploadReport.warnings?.length > 0 && <p className="text-amber-700">Warnings: {uploadReport.warnings.join("; ")}</p>}
          </div>
        )}

        <div className="overflow-x-auto rounded-lg border border-[#E4E9F3]">
          <table data-testid="backups-table" className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="border-b border-[#E4E9F3] bg-[#F5F8FF] text-left text-xs uppercase tracking-wider text-[#5A6B8C]">
                <th className="px-4 py-3 font-medium">Filename</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 font-medium">Size</th>
                <th className="px-4 py-3 font-medium">Collections</th>
                <th className="px-4 py-3 font-medium">Records</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {backups.length === 0 ? (
                <tr><td colSpan={8} className="py-8 text-center text-[#5A6B8C]">No backups yet.</td></tr>
              ) : backups.map((b) => (
                <tr key={b.filename} data-testid={`backup-row-${b.filename}`} className="border-b border-[#EEF2FB] last:border-0">
                  <td className="px-4 py-3 font-mono text-xs text-[#14245F]">{b.filename}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="border-[#DCE3F2] bg-[#EEF2FB] text-[#5A6B8C]">{b.kind}</Badge></td>
                  <td className="px-4 py-3 text-xs text-[#5A6B8C]">{fmt(b.created_at)}</td>
                  <td className="px-4 py-3 text-xs">{kb(b.size_bytes)}</td>
                  <td className="px-4 py-3 text-center">{b.collection_count ?? "—"}</td>
                  <td className="px-4 py-3 text-center">{b.total_records ?? "—"}</td>
                  <td className="px-4 py-3"><ValidationBadge status={b.validation} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button data-testid={`download-${b.filename}`} size="sm" variant="ghost" className="h-8 gap-1 text-[#5A6B8C]" onClick={() => api.downloadBackup(b.filename)}><Download size={14} /></Button>
                      <Button data-testid={`validate-${b.filename}`} size="sm" variant="ghost" className="h-8 gap-1 text-[#5A6B8C]" onClick={() => validate(b.filename)}><FileCheck size={14} /></Button>
                      <Button data-testid={`restore-${b.filename}`} size="sm" variant="ghost" className="h-8 gap-1 text-[#14245F]" onClick={() => setRestoreFor(b)}><RotateCcw size={14} /></Button>
                      <Button data-testid={`delete-backup-${b.filename}`} size="sm" variant="ghost" className="h-8 gap-1 text-red-600" onClick={() => del(b.filename)}><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <RestoreDialog backup={restoreFor} onClose={() => setRestoreFor(null)} onDone={load} />
    </>
  );
}

function RestoreDialog({ backup, onClose, onDone }) {
  const { logout } = useUser();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [text, setText] = useState("");
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!backup) return;
    setPreview(null); setPassword(""); setText(""); setConfirmReplace(false); setLoading(true);
    api.restorePreview(backup.filename).then(setPreview).catch(() => {}).finally(() => setLoading(false));
  }, [backup]);

  const canRestore = password && text === "RESTORE" && confirmReplace && preview?.validation?.status !== "invalid";

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.restore({ filename: backup.filename, password, confirm_text: text, confirm_replace: confirmReplace });
      toast.success(`Restore complete. Safety backup: ${res.safety_backup}`);
      onClose(); onDone();
    } catch (e) {
      const d = e.response?.data?.detail; if (typeof d === "string") toast.error(d);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!backup} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="restore-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#14245F]"><RotateCcw size={18} /> Restore from backup</DialogTitle>
          <DialogDescription>This performs a full database replacement. A safety backup of current data is created first.</DialogDescription>
        </DialogHeader>

        {loading ? <div className="py-8 text-center"><Loader2 className="inline h-5 w-5 animate-spin text-[#1D4ED8]" /></div> : preview && (
          <div className="space-y-4">
            <div data-testid="restore-preview" className="rounded-lg border border-[#E4E9F3] bg-[#F9FBFF] p-3 text-sm">
              <div className="mb-2 flex items-center gap-2">Backup validity: <ValidationBadge status={preview.validation?.status} /></div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-[#5A6B8C]">
                <span><b>Backup date:</b> {fmt(preview.backup_date)}</span>
                <span><b>Source version:</b> {preview.app_version}</span>
                <span><b>Collections:</b> {preview.collection_count}</span>
                <span><b>Incoming records:</b> {preview.total_records}</span>
                <span><b>Soft-deleted in backup:</b> {preview.soft_deleted_records}</span>
                <span><b>Audit records:</b> {preview.audit_record_count}</span>
              </div>
              {preview.validation?.warnings?.length > 0 && <p className="mt-2 text-amber-700">Warnings: {preview.validation.warnings.join("; ")}</p>}
              {preview.validation?.errors?.length > 0 && <p className="mt-2 text-red-700">Errors: {preview.validation.errors.join("; ")}</p>}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Re-enter your administrator password</Label>
              <Input type="password" data-testid="restore-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Type RESTORE to confirm</Label>
              <Input data-testid="restore-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="RESTORE" />
            </div>
            <label className="flex items-start gap-2 text-sm text-[#5A6B8C]">
              <Checkbox data-testid="restore-confirm-replace" checked={confirmReplace} onCheckedChange={(v) => setConfirmReplace(!!v)} className="mt-0.5" />
              I understand all current live data will be replaced by this backup.
            </label>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="restore-confirm" onClick={submit} disabled={!canRestore || busy} className="bg-[#DC2626] hover:bg-[#B91C1C]">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Restore database"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
